import {
  apply, put, select, delay, all, spawn,
} from 'redux-saga/effects';
import moment from 'moment';
import {
  loginAction,
  resetPasswordAction,
  activateAccountAction,
  confirmEmailAction,
  refreshAccessTokenAction,
  changePasswordAction,
  logoutAction,
} from '../reducers/auth.reducers';


const errorMessages = {
  21: 'Das Passwort muss mindestens 6 Zeichen lang sein.',
  23: 'Ungültige E-Mail Adresse',
  // eslint-disable-next-line max-len
  121: 'Du hast dich bereits zuvor mit dieser E-Mail Adresse angemeldet. Gib dein zuvor vergebenes Passwort ein oder verwende eine andere E-Mail Adresse um fortzufahren',
  217: 'Aktivierungscode oder Kundennummer falsch.',
  218: 'Aktivierungslink wurde bereits verwendet.',
};

function setAccessTokenForLegacyPages(accesstoken) {
  sessionStorage.setItem('accessToken', accesstoken);
}

function* checkAutoLogoutTimeout(timeOutLimit) {
  const now = moment();
  if (now.isAfter(moment(timeOutLimit))) {
    yield put(logoutAction());
    return;
  }

  yield delay(60000);
  yield spawn(checkAutoLogoutTimeout, timeOutLimit);
}

function* loginSuccess(response, allowedRoles, onSuccessEffects, autoLogoutTimeoutHours = {}) {
  const {
    user, access_token: accessToken, refresh_token: refreshToken, expires_in: expiresIn, roles,
  } = response;


  if (!roles.some(r => allowedRoles.includes(r))) {
    yield put(loginAction.FAILED({
      message: 'Fehlende Berechtigung',
    }));
    return;
  }

  setAccessTokenForLegacyPages(accessToken);

  yield put(loginAction.DONE({
    user,
    accessToken,
    refreshToken,
    expiresIn,
    roles,
  }));

  if (roles.includes('Admin')) {
    const timeOutLimit = moment().add(autoLogoutTimeoutHours.amount || 12, autoLogoutTimeoutHours.type || 'hours');
    yield spawn(checkAutoLogoutTimeout, timeOutLimit);
  }

  yield all(onSuccessEffects);
}

export const createLoginSaga = (allowedRoles, onSuccessEffects) => function* login(authApi, { payload }) {
  const credentials = payload;
  const response = yield apply(
    authApi,
    authApi.login,
    [credentials.userName, credentials.password],
  );

  if (response.errorCode) {
    yield put(loginAction.FAILED({
      message: response.message || 'Login fehlgeschlagen',
    }));
  } else {
    yield loginSuccess(response, allowedRoles, onSuccessEffects, credentials.autoLogoutTimeoutHours);
  }
};

export function* logout() {
  yield window.localStorage.clear();
  yield window.sessionStorage.clear();
}

export const createChangePasswordSaga = (onSuccessEffects, onErrorEffects) => function* changePassword(userApi, { payload }) {
  const response = yield apply(userApi, userApi.changePassword, [payload]);

  if (response !== '' && JSON.parse(response).errorCode) {
    yield put(changePasswordAction.FAILED());
    yield all(onErrorEffects);
  } else {
    yield put(changePasswordAction.DONE());
    yield all(onSuccessEffects);
  }
};

export const createResetPasswordSaga = onSuccessEffects => function* resetPassword(authApi, { payload }) {
  const result = yield apply(
    authApi,
    authApi.resetPassword,
    [payload],
  );

  if (result.errorCode) {
    yield put(resetPasswordAction.FAILED({ message: 'Passwort konnte nicht zurückgesetzt werden' }));
  } else {
    yield put(resetPasswordAction.DONE());
    yield all(onSuccessEffects);
  }
};

export const createActivateAccountSaga = (allowedRoles, onExistingAccountEffects, onNewAccountEffects) => (
  function* activateAccount(authApi, { payload }) {
    const response = yield apply(authApi, authApi.activateAccount, [payload]);

    if (response.errorCode) {
      yield put(activateAccountAction.FAILED(
        errorMessages[response.errorCode] || 'Account konnte nicht aktiviert werden',
      ));
      return;
    }

    if (response.existingAccount) {
      const loginResponse = yield apply(
        authApi,
        authApi.login,
        [payload.username, payload.newPassword],
      );

      if (response.errorCode) {
        yield put(loginAction.FAILED({
          message: response.message || 'Failed to login',
        }));
        return;
      }

      yield loginSuccess(loginResponse, allowedRoles, []);
      yield put(activateAccountAction.DONE());
      yield all(onExistingAccountEffects);
    } else {
      yield put(activateAccountAction.DONE());
      yield all(onNewAccountEffects);
    }
  }
);

export const createConfirmEmailSaga = (allowedRoles, onSuccessEffects, onFailEffects) => (
  function* confirmEmail(authApi, { payload }) {
    const response = yield apply(authApi, authApi.confirmEmail, [payload]);

    if (response.errorCode) {
      yield put(loginAction.FAILED({
        message: 'Failed to login',
      }));
      yield put(confirmEmailAction.FAILED(errorMessages[response.errorCode] || response.message || 'Login-link ungültig'));
      yield all(onFailEffects);
    } else {
      yield loginSuccess(response, allowedRoles, []);
      yield put(confirmEmailAction.DONE());
      yield all(onSuccessEffects);
    }
  });


let isRefreshing = false;
export function* refreshAccessToken(authApi) {
  const exit = isRefreshing;

  // do not execute refresh task twice
  while (isRefreshing) {
    yield delay(500);
  }

  if (exit) {
    return;
  }

  // refresh token
  isRefreshing = true;
  const refreshToken = yield select(state => state.authentication.refreshToken);

  const response = yield apply(authApi, authApi.refreshAccessToken, [refreshToken]);
  if (response.error || response.errorCode) {
    yield put(refreshAccessTokenAction.FAILED({
      message: response.error_description || response.message || 'Failed to refresh token',
    }));
  } else {
    yield put(refreshAccessTokenAction.DONE({
      refreshToken: response.refresh_token,
      accessToken: response.access_token,
      expiresIn: response.expires_in,
    }));
  }

  isRefreshing = false;
}
